import { MotifButton, MotifIcon, MotifTooltip } from "@ey-xd/motif-react";
import {
  IconoirCheck,
  IconoirXmark,
  IconoirHelpCircle,
} from "@ey-xd/motif-icon";
import "./ReturnOrdersTable.scss";
import orderEmptyImg from "../../../assets/images/empty_order_pipeline.svg";
import EmptyOrder from "../../EmptyOrder";
import {
  NO_RETURN_ORDERS_HEADING,
  NO_RETURN_ORDERS_TEXT,
} from "../../../Constant";
import {
  formatCurrency,
  getFormattedDate,
  getFormattedDateByString,
} from "../helper";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { AppDispatch, RootState } from "../../../store";
import { fetchSkuConfigData } from "../../../store/slices/CTDBSkuConfigSlice";
import Loader from "../../Loader/Loader";
import NoSearchResult from "../../NoSearchResult/NoSearchResult";
import RejectModal from "../ApprovalsTable/RejectModal/RejectModal";
import ApproveReturnModal, {
  SelectedReturnData,
} from "../ApproveReturnModal/ApproveReturnModal";
import { ReactNode, useEffect, useRef, useState } from "react";
import { processReturnApprovalOrReject } from "../../../store/slices/ReturnBMOApprovalSlice";
import {
  APPROVE_RETURN,
  REJECT_RETURN,
} from "../../../pages/BMOSkuApproval/Constants";
import { containsEmoji } from "../../../utils/common";
import { TransactionReturn } from "../types";
import { unitCostInfo } from "../../../utils/skuTable";
import {
  approvalDateInfo,
  rejectionDateInfo,
  rejectionReasonInfo,
  returnReasonInfo,
  transactionDateInfo,
  usagesIntervalInfo,
  usagesPeriodInfo,
} from "../../../utils/returnTable";
import { isEmpty } from "../../../utils/Validator";

export interface ColumnConfig {
  key: string;
  label: string | React.ReactNode;
  render?: (row: any) => React.ReactNode;
}

interface ReturnOrdersTableProps {
  data: any[];
  columnsData: ColumnConfig[];
  setOrders?: React.Dispatch<React.SetStateAction<any[]>>;
  setShowPagination: React.Dispatch<React.SetStateAction<boolean>>;
  searchText: string;
  fetchReturnList: () => void;
  removeOrderPipelineQueryParams: () => void;
  showToastSuccess: (msg: string) => void;
  isFilterApplied: boolean;
}

const ReturnOrdersTable: React.FC<ReturnOrdersTableProps> = ({
  data,
  columnsData,
  setShowPagination,
  searchText,
  fetchReturnList,
  removeOrderPipelineQueryParams,
  showToastSuccess,
  isFilterApplied,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const hasReturnIdQueryParam = Boolean(
    new URLSearchParams(location.search).get("ReturnId"),
  );
  const [showApproveReturnModal, setShowApproveReturnModal] =
    useState<boolean>(false);
  const [showRejectReturnModal, setShowRejectReturnModal] =
    useState<boolean>(false);
  const [selectedTransactionReturnId, setSelectedTransactionReturnId] =
    useState<number | null>(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<
    number | null
  >(null);
  const [selectedReturnData, setSelectedReturnData] = useState({});
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [rejectReasonApiError, setRejectReasonApiError] = useState("");
  const tableRef = useRef<HTMLTableElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [fixedHeader, setFixedHeader] = useState(false);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      if (!tableRef.current) return;
      const tableTop = tableRef.current.getBoundingClientRect().top;
      setFixedHeader(tableTop < 0);
    };
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (!tableRef.current) return;
    const firstRow = tableRef.current.querySelector("tr");
    if (!firstRow) return;
    const widths: number[] = Array.from(firstRow.children).map(
      (cell: any) => cell.getBoundingClientRect().width,
    );
    setColumnWidths(widths);
  }, [data, columnsData]);

  const syncScroll = () => {
    if (tableScrollRef.current) {
      const scrollLeft = tableScrollRef.current.scrollLeft;
      const fixedHeaderTable =
        document.querySelector<HTMLTableElement>(".fixed-header");
      if (fixedHeaderTable) {
        fixedHeaderTable.style.transform = `translateX(-${scrollLeft}px)`;
      }
    }
  };

  const handleApprove = (order: TransactionReturn) => {
    const {
      TransactionReturnId,
      TransactionId,
      OrderTransactionQuantity,
      RequestQuantity,
      SkuId,
      OrderTransactionTotalCost,
      UsageInterval,
      UnitCost,
      ReqReturnAmount,
      Metric,
      UnitsUsed,
      ReturnableUnit,
    } = order;

    const CostMetricCd = order.OrderDetails?.[0]?.SkuDetails?.filter(
      (sku) => sku.SkuId == order.SkuId,
    )?.[0]?.CostMetricCd;

    setSelectedReturnData({
      TransactionReturnId,
      TransactionId,
      OrderTransactionQuantity,
      RequestQuantity,
      SkuId,
      OrderTransactionTotalCost,
      UsageInterval,
      UnitCost,
      ReqReturnAmount,
      Metric,
      CostMetricCd,
      UnitsUsed,
      ReturnableUnit,
    });

    setShowApproveReturnModal(true);
    setSelectedTransactionReturnId(TransactionReturnId);
    setSelectedTransactionId(TransactionId);
  };

  const handleReject = (transactionReturnId: number, transactionId: number) => {
    setRejectReasonApiError("");
    setSelectedTransactionReturnId(transactionReturnId);
    setSelectedTransactionId(transactionId);
    setShowRejectReturnModal(true);
  };

  const rejectReturn = (rejectSKU: string) => {
    setRejectReasonApiError("");
    dispatch(
      processReturnApprovalOrReject({
        Action: REJECT_RETURN,
        RejectReason: rejectSKU,
        TransactionReturnId: selectedTransactionReturnId,
        TransactionId: selectedTransactionId,
      }),
    ).then((action: any) => {
      if (action.type.endsWith("fulfilled")) {
        if (hasReturnIdQueryParam) {
          removeOrderPipelineQueryParams();
        }
        fetchReturnList();
        setRejectReasonApiError("");
        setShowRejectReturnModal(false);
        showToastSuccess(
          hasReturnIdQueryParam
            ? "SKU return rejected."
            : "Return has been rejected successfully",
        );
      } else if (action.type.endsWith("rejected")) {
        setRejectReasonApiError(
          action.payload?.message || "Failed to reject return transaction",
        );
        showToastSuccess("");
        console.log(
          "Error rejecting SKU:",
          action.payload?.message || "Failed to reject return transaction",
        );
      }
    });
  };

  const approveReturn = (selectedApproveType: string, approvedQty: number) => {
    setIsApiLoading(true);
    const isPartialRefund = selectedApproveType === "Partial Refund";
    const payload = {
      Action: APPROVE_RETURN,
      IsFullRefund: !isPartialRefund,
      TransactionReturnId: selectedTransactionReturnId,
      TransactionId: selectedTransactionId,
      ...(isPartialRefund ? { ApproveQuantity: approvedQty } : {}),
    };

    dispatch(processReturnApprovalOrReject(payload)).then((action: any) => {
      setIsApiLoading(false);
      if (action.type.endsWith("fulfilled")) {
        if (hasReturnIdQueryParam) {
          removeOrderPipelineQueryParams();
        }
        fetchReturnList();
        setShowApproveReturnModal(false);
        showToastSuccess(
          hasReturnIdQueryParam
            ? "SKU return approved."
            : "Return has been approved successfully",
        );
      } else if (action.type.endsWith("rejected")) {
        console.log(
          "Error approving return:",
          action.payload?.message || "Failed to approve RETURN transaction",
        );
      }
    });
  };

  const configData = useSelector(
    (state: RootState) => state.CTDBSkuConfigSlice.data,
  );
  const userPermissions: string[] = useSelector(
    (state: RootState) => state.UserProfileSlice.userPermissions,
  );
  const isBMOUser: boolean = userPermissions?.includes("system_bmo");

  const renderNoSearchResult = () => {
    if (isFilterApplied) {
      const header = searchText
        ? `No result under the filter and by the keyword "${searchText}"`
        : `No result under the filter`;

      const message = searchText
        ? "Did you not find any results? We recommend refining your search criteria and/or broadening your filter parameters to enhance your search outcomes."
        : "Did not find any results? We recommend broadening your filter criteria for a more comprehensive search.";

      return (
        <NoSearchResult
          searchValue={searchText}
          header={header}
          message={message}
        />
      );
    } else {
      return <NoSearchResult searchValue={searchText} />;
    }
  };

  if (Array.isArray(data) && data?.length === 0) {
    const hasEmoji = containsEmoji(searchText);
    if ((!searchText && !isFilterApplied) || hasEmoji) {
      return (
        <EmptyOrder
          image={orderEmptyImg}
          header={NO_RETURN_ORDERS_HEADING}
          subHeader={NO_RETURN_ORDERS_TEXT}
        />
      );
    }
    return renderNoSearchResult();
  } else {
    if (configData === null && data && data.length > 0) {
      dispatch(fetchSkuConfigData());
    }

    if (!configData) {
      return <Loader />;
    } else {
      setShowPagination(true);

      const getClassNamesReturnTable = (key: string) => {
        switch (key) {
          case "TransactionReturnId":
            return "return-transaction-return-id";
          case "OrderName":
            return "return-order-name";
          case "SKUName":
            return "return-sku-name";
          case "ReqReturnAmount":
            return "return-req-return-amount";
          case "ApprReturnAmount":
            return "return-appr-return-amount";
          case "TransactionReturnRequestedDate":
            return "return-transaction-return-requested-date";
          case "ReturnType":
            return "return-return-type";
          case "TransactionStatus":
            return "return-transaction-status";
          case "ApprovedOrRejectedDate":
            return "return-approved-or-rejected-date";
          default:
            return "";
        }
      };
      const renderUsagePeriodInTwoLines = (
        startDate: string,
        endDate: string,
      ) => {
        return (
          <div className="usage-period-wrapper">
            <div className="date-line">{`${startDate} -`}</div>
            <div className="date-line">{endDate}</div>
          </div>
        );
      };
      const getReturnColumnClass = (status: string) => {
        switch (status) {
          case "Approved":
            return "return-approved";
          case "Pending":
            return "return-pending";
          case "Rejected":
            return "return-rejected";
          default:
            return "";
        }
      };
      const renderHeaderWithTooltip = (
        label: string,
        tooltipContent: ReactNode,
      ) => {
        return (
          <div className="header-with-tooltip">
            <span>{label}</span>
            <MotifTooltip
              contentClassName="tooltip-content return-modal-tooltip-content"
              allowHover
              hideArrow
              trigger={
                <span className="tooltip-icon-wrapper">
                  <MotifIcon
                    iconFunction={IconoirHelpCircle}
                    size="16"
                    fill="#AFAEBA"
                  />
                </span>
              }
            >
              <>
                <div className="info-content">{tooltipContent}</div>
              </>
            </MotifTooltip>
          </div>
        );
      };
      return (
        <div
          className="return-table-container"
          style={{ position: "relative" }}
        >
          {fixedHeader && (
            <div
              className="fixed-header-wrapper"
              style={{
                overflow: "hidden",
                width: tableScrollRef.current?.getBoundingClientRect().width,
              }}
            >
              <table
                className="motif-table-standard fixed-header"
                style={{ width: columnWidths.reduce((a, b) => a + b, 0) }}
              >
                <thead>
                  <tr>
                    {columnsData.map((col, index) => (
                      <th key={col.key} style={{ width: columnWidths[index] }}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
          )}

          <div
            className="approval-list-table"
            ref={tableScrollRef}
            style={{ overflowX: "auto" }}
            onScroll={syncScroll}
          >
            <table className="motif-table-standard" ref={tableRef}>
              <thead>
                <tr>
                  {columnsData.map((col) => (
                    <th
                      key={col.key}
                      className={getClassNamesReturnTable(col.key)}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data?.map((order) => {
                  const uagesDates = order.UsagePeriod?.split("-");
                  const startDate = uagesDates?.[0].trim() || "";
                  const endDate = uagesDates?.[1].trim() || "";
                  return (
                    <>
                      <tr
                        className={
                          order.isSKUExpanded ? "no-border-bottom" : ""
                        }
                      >
                        {columnsData.map((col) => (
                          <td key={col.key}>
                            {col.render ? col.render(order) : order[col.key]}
                          </td>
                        ))}
                      </tr>
                      <tr
                        className={
                          order.isSKUExpanded
                            ? "product-list-wrapper"
                            : "product-list-wrapper hide-details"
                        }
                      >
                        <td
                          colSpan={9}
                          className="product-list order-success-content"
                        >
                          <>
                            <div className="view-detail-wrapper">
                              <table className="motif-table-standard request-table">
                                <thead>
                                  <tr>
                                    <th className="usages-period">
                                      {renderHeaderWithTooltip(
                                        "Usage period",
                                        usagesPeriodInfo(),
                                      )}
                                    </th>
                                    <th className="usages-interval">
                                      {renderHeaderWithTooltip(
                                        "Usage interval",
                                        usagesIntervalInfo(),
                                      )}
                                    </th>
                                    <th className="unit-cost">
                                      {renderHeaderWithTooltip(
                                        "Unit cost",
                                        unitCostInfo(),
                                      )}
                                    </th>
                                    {order.TransactionStatus === "Rejected" && (
                                      <th className="rejection-date">
                                        {renderHeaderWithTooltip(
                                          "Rejection date",
                                          rejectionDateInfo(),
                                        )}
                                      </th>
                                    )}
                                    {order.TransactionStatus === "Approved" && (
                                      <th className="approval-date">
                                        {renderHeaderWithTooltip(
                                          "Approval date",
                                          approvalDateInfo(),
                                        )}
                                      </th>
                                    )}
                                    <th
                                      className={`return-reason ${getReturnColumnClass(order.TransactionStatus)}`}
                                    >
                                      {renderHeaderWithTooltip(
                                        "Return reason",
                                        returnReasonInfo(),
                                      )}
                                    </th>
                                    {order.TransactionStatus === "Rejected" && (
                                      <th className="rejection-reason">
                                        {renderHeaderWithTooltip(
                                          "Rejection reason",
                                          rejectionReasonInfo(),
                                        )}
                                      </th>
                                    )}
                                    {order.TransactionStatus === "Approved" && (
                                      <th className="transaction-date">
                                        {renderHeaderWithTooltip(
                                          "Transaction date",
                                          transactionDateInfo(),
                                        )}
                                      </th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr key={order.TransactionReturnId}>
                                    <td>
                                      {renderUsagePeriodInTwoLines(
                                        getFormattedDateByString(startDate),
                                        getFormattedDateByString(endDate),
                                      )}
                                    </td>
                                    <td className="usage-interval-cell">{`${order.UsageInterval}`}</td>
                                    <td className="unit-cost">
                                      <div>
                                        {formatCurrency(
                                          order.OrderTransactionUnitCost,
                                        )}
                                      </div>
                                      <div className="secondary-value">
                                        Per {order.Metric.slice(0, -1)}
                                      </div>
                                    </td>
                                    {(order.TransactionStatus === "Rejected" ||
                                      order.TransactionStatus ===
                                        "Approved") && (
                                      <td>
                                        {getFormattedDate(
                                          order.ApprovedOrRejectedDate,
                                        )}
                                      </td>
                                    )}
                                    <td>{order.ReturnReason}</td>
                                    {order.TransactionStatus === "Rejected" && (
                                      <td>{order.ResponseReason}</td>
                                    )}
                                    {order.TransactionStatus === "Approved" && (
                                      <td>
                                        {isEmpty(order.OrderTransactionDate) ||
                                        order.OrderTransactionDate === "N/A"
                                          ? "--"
                                          : getFormattedDate(
                                              order.OrderTransactionDate,
                                            )}
                                      </td>
                                    )}
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div className="product-list-footer">
                              <div className="action-btns">
                                {isBMOUser && (
                                  <>
                                    <MotifButton
                                      onClick={() => handleApprove(order)}
                                      disabled={
                                        order.IsApprovedOrRejected === 1
                                      }
                                      className="order-approve-btn"
                                    >
                                      <MotifIcon
                                        iconFunction={IconoirCheck}
                                        size="16"
                                      />
                                      Approve
                                    </MotifButton>
                                    <MotifButton
                                      onClick={() =>
                                        handleReject(
                                          order.TransactionReturnId,
                                          order.TransactionId,
                                        )
                                      }
                                      disabled={
                                        order.IsApprovedOrRejected === 1
                                      }
                                      className="order-reject-btn"
                                    >
                                      <MotifIcon
                                        iconFunction={IconoirXmark}
                                        size="16"
                                      />
                                      Reject
                                    </MotifButton>
                                  </>
                                )}
                              </div>
                            </div>
                          </>
                        </td>
                      </tr>

                      <tr
                        className={
                          order.isOrderExpanded
                            ? "product-list-wrapper"
                            : "product-list-wrapper hide-details"
                        }
                      >
                        <td
                          colSpan={9}
                          className="request-list order-success-content"
                        ></td>
                      </tr>
                    </>
                  );
                })}
              </tbody>

              <ApproveReturnModal
                show={showApproveReturnModal}
                setShow={setShowApproveReturnModal}
                btnLabel="Approve Return"
                approveHandler={approveReturn}
                selectedReturnData={selectedReturnData as SelectedReturnData}
                isApiLoading={isApiLoading}
              />
              <RejectModal
                show={showRejectReturnModal}
                setShow={setShowRejectReturnModal}
                btnLabel="Reject Return"
                rejectMessage="You are about to reject the return for this SKU, please enter a valid rejection reason for the stakeholders involved:"
                rejectHandler={(rejectMsg) => rejectReturn(rejectMsg)}
                apiErrorMessage={rejectReasonApiError}
                onClearApiError={() => setRejectReasonApiError("")}
              />
            </table>
          </div>
        </div>
      );
    }
  }
};
export default ReturnOrdersTable;
